import { HeroFieldsFragment } from "@src/generated/graphql";
import { highlightUnderscoreText } from "src/utils/utils";
import { FaArrowRightLong } from "react-icons/fa6";
import { DynamicLink, Typography } from "src/components/shared";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import { useEffect, useState, useRef } from "react";

type HeroComponentProps = {
  content: HeroFieldsFragment;
};

const HeroComponent = ({ content }: HeroComponentProps) => {
  const {
    title,
    heroFieldDescriptionJson,
    heroImage,
    primaryButton,
    titleRtf,
    backgroundImage,
  } = content;
  const descriptionText =
    heroFieldDescriptionJson?.json?.content?.[0]?.content?.[0]?.value ?? "";

  const [animationData, setAnimationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSecondaryContent, setShowSecondaryContent] = useState(false);
  const [initialTitleVisible, setInitialTitleVisible] = useState(false);
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const animationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [windowDimensions, setWindowDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  });
  // Calculate useMobile directly from windowDimensions rather than as a separate state
  const useMobile = windowDimensions.width < 780;

  useEffect(() => {
    const fetchAnimationData = async () => {
      if (heroImage?.url) {
        try {
          const response = await fetch(heroImage.url);
          if (!response.ok) {
            throw new Error(`Failed to fetch: ${response.status}`);
          }
          const data = await response.json();
          setAnimationData(data);
        } catch (error) {
          console.error("Error fetching Lottie animation:", error);
        } finally {
          setIsLoading(false);
          setTimeout(() => setInitialTitleVisible(true), 200);
        }
      }
    };

    fetchAnimationData();
  }, [heroImage?.url]);

  useEffect(() => {
    // Function to update dimensions
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setWindowDimensions({ width, height });
    };
  
    // Call it immediately on mount if window is defined
    if (typeof window !== 'undefined') {
      handleResize();
    }
    
    // Add event listener
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
    }
    
    // Cleanup
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);


  const handleAnimationLoaded = () => {
    if (lottieRef.current) {
      const totalDuration = lottieRef.current.getDuration(false);

      if (typeof totalDuration === "number") {
        const halfDuration = totalDuration / 2 - 1.5;

        animationTimerRef.current = setTimeout(() => {
          setInitialTitleVisible(false);

          setTimeout(() => {
            setShowSecondaryContent(true);
          }, 300);
        }, halfDuration * 1000);
      } else {
        console.warn("Could not determine animation duration, using default");
        animationTimerRef.current = setTimeout(() => {
          setInitialTitleVisible(false);
          setTimeout(() => {
            setShowSecondaryContent(true);
          }, 300);
        }, 2000);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
      }
    };
  }, []);
  
  return (
    <div className="hero-block">
      <div className="hero-graphics"></div>
      <div className="hero-banner-block hero-background lottie-banner-block">
        <img
          src={backgroundImage?.url ?? ""}
          alt="background"
          className="background-image"
        />
        <div className="container-fluid position-relative" >
          <div style={{color:"#fff"}} className="lottie-container">
            <div>
              {heroImage?.url && !isLoading && animationData && (
                <Lottie
                  lottieRef={lottieRef}
                  animationData={animationData}
                  loop={true}
                  onLoadedImages={handleAnimationLoaded}
                  className="lottie"
                />
              )}
              <div 
                className="content-container lottie-anim-text-container"
              >
                  <div className="content-transition-wrapper">
                    <h1
                      className="hero-title hero-title-custom light"
                      dangerouslySetInnerHTML={{
                        __html: highlightUnderscoreText(titleRtf ?? title ?? ""),
                      }}
                    />

                    {descriptionText && (
                      <Typography
                        tag="p"
                        className="hero-description text-start light slide-up"
                      >
                        {descriptionText}
                      </Typography>
                    )}

                    {primaryButton && (
                      <DynamicLink
                        url={primaryButton.buttonLink ?? ""}
                        className="btn-white "
                      >
                        <span>{primaryButton.buttonText ?? ""}</span>
                        <FaArrowRightLong className="btn-icon" />
                      </DynamicLink>
                    )}
                  </div>
              </div>
            </div>
           
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroComponent;